import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { InspectionAssemblyItem } from 'src/app/api/elite/types/inspection-assembly-item';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { PaginatorModule } from 'primeng/paginator';
import { ReactiveFormsModule } from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { MatStepperModule } from '@angular/material/stepper';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { AlertService } from 'src/app/core/services/alert.service';
import { QualityControlInspectionDataDetailsService } from './data-measurement.service';
import { QualityControlInspectionDataFormMeasureValuesComponent } from './form-measure-values/form-measure-values.component';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { map, startWith, take } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { Certificate } from '../../../../api/elite/types/certificate';
import { ChipsInputComponent } from '../../../../shared/components/chips-input/chips-input.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { RoutingService } from 'src/app/core/services/routing.service';

export type QualityControlInspectionDataDetailsTypes = {
  data: DialogData;
  result: {
    assemblyName: string;
    assemblyItemId?: number;
    urlDrive: string;
    measureValues: {
      min: number;
      expected: number;
      max: number;
      type: string;
      group: number;
      measureToolId: number;
      timesToMeasure?: number;
    }[];
    aqlTypeId?: number;
    selectedTests: number[];
    certificateIds: number[];
    measureInspection: boolean;
    visualInspection: boolean;
    cobotInspection: boolean;
    specialLabelId?: number;
    quantityPerInspection?: number;
    quantityToInspect?: number;
  };
};
type DialogData = {
  item: InspectionAssemblyItem;
  specificGroups?: number;
  itemName?: string;
  toolTypes?: { id: number; title: string }[];
  showTimesToMeasure: boolean;
  showQuantityPerInspection?: boolean;
  isReadonly: boolean;
};

@Component({
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    PaginatorModule,
    ReactiveFormsModule,
    MatOptionModule,
    MatSelectModule,
    TranslateModule,
    MatIconModule,
    QualityControlInspectionDataFormMeasureValuesComponent, // here
    MatStepperModule,
    ChipsInputComponent,
    MatSlideToggleModule,
    NgxMatSelectSearchModule,
    MatTooltipModule,
    MatCheckboxModule,
  ],
  providers: [QualityControlInspectionDataDetailsService],
  templateUrl: './data-measurement.component.html',
  styleUrls: ['./data-measurement.component.sass'],
})
export class QualityControlInspectionDataDetailsComponent
  implements OnInit, OnDestroy
{
  private readonly service = inject(QualityControlInspectionDataDetailsService);
  private readonly dialogRef = inject(MatDialogRef);
  public readonly data: DialogData = inject(MAT_DIALOG_DATA);
  private readonly alertService = inject(AlertService);
  protected readonly showQuantityPerInspection =
    this.data.showQuantityPerInspection;
  private readonly routerService = inject(RoutingService);

  mainForm = this.service.mainForm;

  measureForm$ = this.service.measuresFormSelected$;
  selectedGroupControl = this.service.selectedGroupControl;

  leftOptionsToSelect$ = this.service.leftOptionsToSelect$;
  inspectionTests$ = this.service.inspectionTests$;

  optionsGroup: string[];
  showVisualInspection$ = this.mainForm
    .get('hasVisualInspection')
    .valueChanges.pipe(startWith(!!this.data?.item?.visualInspection));

  optionsLabels$ = this.service.optionsSpecialLabel$;

  showFormForMeasures = false;

  get urlDriveError(): string {
    return this.mainForm.controls.urlDrive?.errors?.pattern || '';
  }

  aqlTypes$ = this.service.aqlTypesFiltered$;

  currentTests = this.data?.item?.testsToDo || [];
  selectedCertificates: Certificate[] = this.data?.item?.certificates || [];
  certificateOptions$ = this.service.certificates$;

  private subscription: Subscription;

  constructor() {}

  ngOnInit(): void {
    this.service.dialogData = this.data.item;
    this.service.enableQtyInspectionInputs = this.showQuantityPerInspection;
    this.initForm();
    if (this.data.item) {
      if (this.data.item.measureInspection === true) {
        this.showFormForMeasures = true;
        this.initGroups();
      }
    }
    this.setControlForm();
    this.subscription = this.service.optionsGroup$.subscribe(
      (optionsGroup) => (this.optionsGroup = optionsGroup)
    );
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  private initForm(): void {
    this.service.initForm(this.data.item);
    if (this.data.specificGroups) {
      this.service.mainForm
        .get('numberOfGroups')
        .setValue(this.data.specificGroups);
      this.service.mainForm.get('numberOfGroups').disable();
    }
    if (this.data && this.data.item && this.data.item.measureInspection) {
      this.mainForm
        .get('hasMeasureInspection')
        ?.setValue(this.data.item.measureInspection);
    }

    this.mainForm
      .get('hasVisualInspection')
      .setValue(!!this.data?.item?.visualInspection);
    this.mainForm
      .get('hasCobotInspection')
      .setValue(this.data?.item?.cobotInspection ?? false);
    this.service.mainForm
      .get('hasMeasureInspection')
      .valueChanges.subscribe((val) => {
        this.service.mainForm
          .get('numberOfGroups')
          .setValue(this.data.specificGroups || 1);
        this.showFormForMeasures = val;
      });
    if (this.data?.item?.visualInspection) {
      this.service.mainForm.get('aqlType').setValue(this.data.item.aqlTypeId);
    }
    this.handleDisableCobotCheckbox();
  }

  // Allow show cobot checkbox only for Compression Molding department
  private handleDisableCobotCheckbox(): void {
    const departmentsAllow = ['compression molding'];
    this.routerService
      .lastRouteData<string>('department')
      .pipe(take(1))
      .pipe(map((dep) => dep?.toLowerCase()))
      .subscribe((dep?: string) => {
        if (!dep || !departmentsAllow.includes(dep)) {
          this.service?.mainForm?.get('hasCobotInspection')?.disable();
        }
      });
  }

  initGroups(): void {
    this.service.mainForm
      .get('numberOfGroups')
      .setValue(this.data.specificGroups || 1);
  }

  setControlForm(): void {
    this.service.setAssemblyName(this.data.itemName);
    if (this.data.isReadonly) {
      this.service.disableForm();
    }
    if (!this.showQuantityPerInspection) {
      this.mainForm.get('quantityPerInspection')?.disable();
      this.mainForm.get('quantityToInspect')?.disable();
    }
  }

  addMeasureForm(): void {
    this.service.addMeasureForm();
  }

  removeMeasureForm(index: number): void {
    this.service.removeMeasure(index);
  }

  onClickSave(): void {
    if (this.mainForm.invalid) {
      this.alertService.error('No todos los campos necesarios estan llenos');
      return;
    }
    const measureInspection = this.mainForm.get('hasMeasureInspection').value;
    const visualInspection = this.mainForm.get('hasVisualInspection').value;
    const cobotInspection = this.mainForm.get('hasCobotInspection').value;
    const assemblyName = this.mainForm.get('assemblyName').value;
    const formData = this.mainForm.value;
    this.dialogRef.close({
      assemblyName,
      urlDrive: formData.urlDrive,
      measureValues: measureInspection ? this.service.getMeasuresData() : [],
      selectedTests: this.currentTests.map((el) => el.id),
      certificateIds: this.selectedCertificates.map((el) => el.id),
      aqlTypeId: visualInspection ? formData.aqlType : undefined,
      measureInspection,
      visualInspection,
      cobotInspection: visualInspection ? cobotInspection : false,
      specialLabelId:
        formData.specialLabelId > 0 ? formData.specialLabelId : null,
      ...this.getInspectionValues(),
    });
  }

  private getInspectionValues(): {
    quantityToInspect: number;
    quantityPerInspection: number;
  } {
    if (!this.showQuantityPerInspection) {
      return;
    }
    return {
      quantityToInspect: this.mainForm.get('quantityToInspect').value,
      quantityPerInspection: this.mainForm.get('quantityPerInspection').value,
    };
  }
}
